<template>
    <v-dialog v-model="display" :width="dialogWidth">
        <template v-slot:activator="{ on }">
            <div :class="{'header-filter': headerFilter}">
                <v-text-field
                        class="date-input"
                        :style="{'--accentText-color': $vuetify.theme.currentTheme.accentText}"
                        :disabled="disabled"
                        :appendIcon="datetime ? 'mdi-filter-remove-outline':''"
                        hide-details
                        dense
                        :label="label"
                        :loading="loading"
                        :value="formattedDatetime"
                        readonly
                        v-bind="textFieldProps"
                        v-on="on"
                        @click:append="clearHandler">
                    <template v-slot:progress>
                        <slot name="progress">
                            <v-progress-linear absolute color="primary" height="2" indeterminate></v-progress-linear>
                            <v-datetime-picker v-model="addLeadingZeros"/>
                        </slot>
                    </template>
                </v-text-field>
            </div>
        </template>
        <v-card>
            <v-card-text class="px-0 py-0">
                <v-date-picker v-model="date" full-width v-bind="datePickerProps"
                               @input="showTimePicker"></v-date-picker>
                <div class="mx-5">
                    <TimeSelector v-model="time"/>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <slot :parent="this" name="actions">
                    <v-btn color="grey lighten-1" text @click.native="clearHandler">{{ clearText }}</v-btn>
                    <v-btn color="green darken-1" text @click="okHandler">{{ okText }}</v-btn>
                </slot>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {format, parse} from 'date-fns';
import TimeSelector from "@/components/reusable/TimeSelector/TimeSelector.vue";

const DEFAULT_DATE = '';
const DEFAULT_TIME = '00:00';
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_TIME_FORMAT = 'HH:mm:ss';
const DEFAULT_DIALOG_WIDTH = 340;
const DEFAULT_CLEAR_TEXT = 'CLEAR';
const DEFAULT_OK_TEXT = 'OK';

export default {
  name: 'v-datetime-picker',
  components: {TimeSelector},
  model: {
    prop: 'datetime',
    event: 'input'
  },
  props: {
    datetime: {
      type: [Date, String],
      default: null
    },
    disabled: {
      type: Boolean
    },
    loading: {
      type: Boolean
    },
    label: {
      type: String,
      default: ''
    },
    dialogWidth: {
      type: Number,
      default: DEFAULT_DIALOG_WIDTH
    },
    dateFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT
    },
    timeFormat: {
      type: String,
      default: 'HH:mm'
    },
    clearText: {
      type: String,
      default: DEFAULT_CLEAR_TEXT
    },
    okText: {
      type: String,
      default: DEFAULT_OK_TEXT
    },
    textFieldProps: {
      type: Object
    },
    datePickerProps: {
      type: Object
    },
    timePickerProps: {
      type: Object
    },
    headerFilter: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      display: false,
      activeTab: 0,
      date: DEFAULT_DATE,
      time: DEFAULT_TIME,
      timeout: null,
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    dateTimeFormat() {
      return this.dateFormat + ' ' + this.timeFormat;
    },
    defaultDateTimeFormat() {
      return DEFAULT_DATE_FORMAT + ' ' + DEFAULT_TIME_FORMAT;
    },
    formattedDatetime() {
      return this.selectedDatetime ? format(this.selectedDatetime, this.dateTimeFormat) : '';
    },
    selectedDatetime() {
      if (this.date && this.time) {
        let datetimeString = this.date + ' ' + this.time;
        if (this.time.length === 5) {
          datetimeString += ':00';
        }
        return parse(datetimeString, this.defaultDateTimeFormat, new Date());
      } else {
        return null;
      }
    },
    dateSelected() {
      return !this.date;
    }
  },
  methods: {
    init() {
      if (!this.datetime) {
        this.date = DEFAULT_DATE;
        this.time = DEFAULT_TIME;
        return;
      }

      let initDateTime;
      if (this.datetime instanceof Date) {
        initDateTime = this.datetime;
      } else if (typeof this.datetime === 'string' || this.datetime instanceof String) {
        // see https://stackoverflow.com/a/9436948
        initDateTime = parse(this.datetime, this.dateTimeFormat, new Date());
      }

      this.date = format(initDateTime, DEFAULT_DATE_FORMAT);
      this.time = format(initDateTime, DEFAULT_TIME_FORMAT);
    },
    okHandler() {
      this.resetPicker();
      this.$emit('input', this.selectedDatetime);
    },
    today() {
      this.date = format(new Date(), DEFAULT_DATE_FORMAT);
      this.$emit('updateDate', this.date);
      this.activeTab = 1;
    },
    now() {
      this.time = format(new Date(), DEFAULT_TIME_FORMAT);
      this.okHandler();
    },
    clearHandler() {
      this.resetPicker();
      this.date = DEFAULT_DATE;
      this.time = DEFAULT_TIME;
      this.$emit('input', null);
    },
    resetPicker() {
      this.display = false;
      this.activeTab = 0;
      if (this.$refs.timer) {
        this.$refs.timer.selectingHour = true;
      }
    },
    showTimePicker() {
      this.$emit('updateDate', this.date);
      this.activeTab = 1;
    },
    addLeadingZeros(num, totalLength) {
      return String(num).padStart(totalLength, '0');
    },
  },
  watch: {
    datetime: function () {
      this.init();
    },
    formattedDatetime: function () {
      this.$emit('input', this.selectedDatetime);
    },
    display(val) {
      if (val) {
        clearTimeout(this.timeout)
        this.$emit('openMenu', val);
      } else {
        this.timeout =  setTimeout(() => {
          this.$emit('openMenu', val);
        }, 300);
      }
    },
    activeTab() {
      if (this.activeTab === 1) {
        if (this.timePickerProps.min) {
          let minTime = this.timePickerProps.min.split(':');
          this.time = this.addLeadingZeros(minTime[0], 2) + ':' + this.addLeadingZeros(minTime[1], 2);
        } else {
          this.time = DEFAULT_TIME;
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.date-input {
  width: 180px;
}

.timer {
  display: flex;
  height: 300px;
}

.header-filter {
  ::v-deep {
    .v-input__control > .v-input__slot:before {
      border-color: var(--accentText-color) !important;

    }

    .v-select__selection, .v-label, .v-text-field__slot input {
      color: var(--accentText-color) !important;
    }

    .v-select__selections {
      color: var(--accentText-color) !important;
      max-height: 52px;
    }

    .v-input__icon--append > .v-icon {
      color: var(--accentText-color);
    }
  }
}
</style>
